import { useEffect, useRef, useState } from 'react'
import { ChevronRight, ChevronDown, AlertTriangle, Loader, Check, AlertCircle } from 'lucide-react'
import { useDeviceStore } from '../../store/device-store'
import { fetchFirmwareProgress } from '../../lib/projectsApi'
import LiveIconTabs from './LiveIconTabs'

// Device fwprogress status → firmware card display. An FTP update runs 10-20 min
// and reports every ~4 KB. Downloading/Verifying/Queued = in progress (button
// disabled); Success/Complete = done; Failure = failed (retry allowed).
function fwProgressView(p) {
  if (!p || !p.status) return null
  const s = String(p.status).toLowerCase()
  const pct = p.progress == null ? '' : `${p.progress}%`
  if (s === 'failure')  return { text: 'Update failed', updating: false, failed: true }
  if (s === 'complete' || s === 'success') return { text: 'Done 100%', updating: false, done: true }
  if (s === 'verifying') return { text: `Verifying ${pct}`.trim(), updating: true }
  // `queued` = command sent but the logger hasn't started the FTP download yet,
  // so it can still be cancelled (flagged for the Cancel button in the card).
  if (s === 'queued')    return { text: 'Queued…', updating: true, queued: true }
  return { text: `Downloading ${pct || '0%'}`, updating: true }   // Downloading
}

/**
 * Data Logger (Live) settings tab — dynamic, laid out to match the Adobe XD design.
 *
 * Layout: small icon tabs, then a two-column body — left: Device Name + Firmware
 * + action buttons; right: Sample/Log/Transmit with a "More" toggle.
 *
 * Reset / Sync Clock queue real device commands. Firmware "Update" and "Format
 * Memory" are disabled with a reason (device-initiated / unconfirmed command).
 * Sample/Log/Transmit values are displayed (read-only); saving is a follow-up.
 */

const SectionHeading = ({ children, right }) => (
  <div className="flex items-center justify-between border-b border-gray-200 pb-4 pt-2 px-4 mb-3">
    <h3 className="common-size18 text-sm font-bold text-[#4D4D4D]">{children}</h3>
    {right}
  </div>
)

// White card container with subtle border + shadow. The XD design sits
// the Device Name / Firmware / Sample-Log-Transmit blocks on a light gray
// page background; this is the per-block surface.
const Card = ({ children, className = '' }) => (
  <div className={`bg-white border-gray-200 rounded-lg shadow-sm px-3 py-4 ${className}`}>
    {children}
  </div>
)

const ActionFeedback = ({ status, message }) => {
  if (!status || status === 'idle') return null
  if (status === 'pending') return <Loader className="w-4 h-4 animate-spin text-gray-400" />
  if (status === 'success') return <span className="inline-flex items-center gap-1 text-xs text-green-600"><Check className="w-3.5 h-3.5" />{message || 'Queued'}</span>
  return <span className="inline-flex items-center gap-1 text-xs text-red-600"><AlertCircle className="w-3.5 h-3.5" />{message || 'Failed'}</span>
}


export default function DataLoggerTab({ projectId, deviceId, currentLiveIndex, setCurrentLiveIndex, devices = [], onSelectDevice, showHeader = true }) {
  const { device, config, isLoading, error, action, loadDevice, updateName, runAction, updateFirmware, cancelFirmware, setIntervals, applyConfig, clearActionStatus } = useDeviceStore()
  const [nameInput, setNameInput] = useState('')
  const [showMore, setShowMore] = useState(false)
  const [expanded, setExpanded] = useState({})
  // Editable device-wide Log/Transmit inputs (collapsed view), seeded from config.
  const [logEdit, setLogEdit] = useState('')
  const [transmitEdit, setTransmitEdit] = useState('')
  // "More" view edit state: per-sensor {log, sample} and per-parameter transmit flags.
  const [sensorEdits, setSensorEdits] = useState({})
  const [flagEdits, setFlagEdits] = useState({})
  // Live FTP firmware-update progress per type ({ base: {...}, rtu: {...} }).
  const [fwProgress, setFwProgress] = useState({})

  useEffect(() => {
    if (projectId && deviceId) loadDevice(projectId, deviceId)
  }, [projectId, deviceId, loadDevice])

  // Poll firmware-update progress while the tab is open (an update runs 10-20 min
  // and the device reports frequently). Lightweight endpoint, so a steady 5s poll
  // keeps the % fresh without waiting on a full device reload.
  useEffect(() => {
    if (!projectId || !deviceId) { setFwProgress({}); return }
    let cancelled = false
    const poll = async () => {
      try {
        const res = await fetchFirmwareProgress(projectId, deviceId)
        if (!cancelled && res && res.success) setFwProgress(res.progress || {})
      } catch { /* transient — next tick retries */ }
    }
    poll()
    const id = setInterval(poll, 5000)
    return () => { cancelled = true; clearInterval(id) }
  }, [projectId, deviceId])

  // Seed the editable fields whenever fresh config arrives.
  useEffect(() => {
    const s = config?.sensors || []
    const lm = s.length ? s[0].log_interval_min : null
    setLogEdit(lm ?? '')
    setTransmitEdit(config?.transmit_interval_min ?? '')
    const se = {}
    const fe = {}
    s.forEach((sensor) => {
      se[sensor.id] = { log: sensor.log_interval_min ?? '', sample: sensor.sample_interval_min ?? '' }
      ;(sensor.parameters || []).forEach((p) => { fe[p.id] = !!p.transmit_flag })
    })
    setSensorEdits(se)
    setFlagEdits(fe)
  }, [config])

  const toggle = (id) => setExpanded((e) => ({ ...e, [id]: !e[id] }))

  const onUpdateName = async () => {
    const trimmed = nameInput.trim()
    if (!trimmed) return
    await updateName(projectId, deviceId, trimmed)
    setNameInput('')
    setTimeout(() => clearActionStatus('name'), 4000)
  }

  const onRunAction = async (key, confirmMsg, arg) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return
    await runAction(key, projectId, deviceId, arg)
    setTimeout(() => clearActionStatus(key), 4000)
  }

  const onUpdateFirmware = async (type, latest) => {
    if (!window.confirm(`Update ${type.toUpperCase()} firmware to the latest version (${latest})?`)) return
    const key = type === 'rtu' ? 'firmwareRtu' : 'firmwareBase'
    const res = await updateFirmware(projectId, deviceId, type)
    if (!res.success) alert(`Firmware update failed: ${res.error}`)
    setTimeout(() => clearActionStatus(key), 5000)
  }

  const onCancelFirmware = async (type) => {
    const res = await cancelFirmware(projectId, deviceId, type)
    if (!res.success) { alert(`Cancel failed: ${res.error}`); return }
    // Drop the Queued progress locally so the card reverts to "Update to Latest"
    // immediately (the backend removed the pending command + Queued row).
    setFwProgress((p) => { const n = { ...p }; delete n[type]; return n })
  }

  const onApplyIntervals = async () => {
    const sensors0 = config?.sensors || []
    const curLog = sensors0.length ? sensors0[0].log_interval_min : null
    const curTx = config?.transmit_interval_min ?? null
    const payload = {}
    // Only push values the user actually changed, to avoid redundant commands.
    if (logEdit !== '' && Number(logEdit) !== curLog) payload.logMin = Number(logEdit)
    if (transmitEdit !== '' && Number(transmitEdit) !== curTx) payload.transmitMin = Number(transmitEdit)
    if (payload.logMin == null && payload.transmitMin == null) {
      clearActionStatus('intervals')
      return
    }
    if (!window.confirm('Send the new Log/Transmit interval(s) to this device?')) return
    await setIntervals(projectId, deviceId, payload)
    setTimeout(() => clearActionStatus('intervals'), 5000)
  }

  const onApplyMore = async () => {
    const cfgSensors = config?.sensors || []
    const payload = {}
    // Device-wide transmit interval.
    const curTx = config?.transmit_interval_min ?? null
    if (transmitEdit !== '' && Number(transmitEdit) !== curTx) payload.transmit_min = Number(transmitEdit)
    // Per-sensor Sample/Log diffs.
    const sensorsPayload = []
    cfgSensors.forEach((s) => {
      const e = sensorEdits[s.id] || {}
      const row = { sensor_id: s.id }
      let changed = false
      if (e.log !== '' && e.log != null && Number(e.log) !== (s.log_interval_min ?? null)) { row.log_min = Number(e.log); changed = true }
      if (e.sample !== '' && e.sample != null && Number(e.sample) !== (s.sample_interval_min ?? null)) { row.sample_min = Number(e.sample); changed = true }
      if (changed) sensorsPayload.push(row)
    })
    if (sensorsPayload.length) payload.sensors = sensorsPayload
    // Per-parameter transmit on/off diffs.
    const flagsPayload = []
    cfgSensors.forEach((s) => (s.parameters || []).forEach((p) => {
      const want = !!flagEdits[p.id]
      if (want !== !!p.transmit_flag) flagsPayload.push({ param_id: p.id, transmit: want })
    }))
    if (flagsPayload.length) payload.transmit_flags = flagsPayload

    if (!payload.transmit_min && !payload.sensors && !payload.transmit_flags) {
      clearActionStatus('intervals')
      return
    }
    if (!window.confirm('Apply these Sample/Log/Transmit changes to the device?')) return
    await applyConfig(projectId, deviceId, payload)
    setTimeout(() => clearActionStatus('intervals'), 5000)
  }

  const sensors = config?.sensors || []

  // Header is rendered in every state (empty / loading / loaded) so the device
  // switcher (inside LiveIconTabs) stays visible while a newly-picked device loads.
  // When the parent renders the shared icon-tab header outside the read-only
  // gate (so navigation stays clickable), it passes showHeader={false}.
  const header = showHeader ? (
    <Card className="!py-2 !border-none">
      <LiveIconTabs
        currentLiveIndex={currentLiveIndex}
        setCurrentLiveIndex={setCurrentLiveIndex}
        devices={devices}
        deviceId={deviceId}
        onSelectDevice={onSelectDevice}
      />
    </Card>
  ) : null

  if (!deviceId) {
    return (
      <div className="w-full h-full flex flex-col bg-[#F1F2F4] overflow-y-auto gap-5">
        {header}
        <div className="p-6 text-sm text-gray-500">Select a device to view its data logger settings.</div>
      </div>
    )
  }
  if (isLoading) {
    return (
      <div className="w-full h-full flex flex-col bg-[#F1F2F4] overflow-y-auto gap-5">
        {header}
        <div className="p-6 flex items-center gap-2 text-sm text-gray-500"><Loader className="w-4 h-4 animate-spin" /> Loading device…</div>
      </div>
    )
  }

  return (
    // Page background is the same light-gray fill the Visualize / Validate /
    // Formulas tabs use, so cards read as distinct surfaces against it.
    <div className="w-full h-full flex flex-col bg-[#F1F2F4] overflow-y-auto gap-5">
      {header}

      {error && (
        <div className="px-3 py-2 bg-red-50 border border-red-200 rounded text-sm text-red-700 flex items-center gap-2">
          <AlertCircle className="w-4 h-4" /> {error}
        </div>
      )}

      {/* Two-column body. Left stacks Device Name + Firmware + action
          buttons; right is Sample/Log/Transmit. Each block is its own
          white card on the gray page. */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 items-start">
        {/* LEFT column */}
        <div className="space-y-5">
          {/* Device Name card */}

          <Card>
            <SectionHeading>Device Name</SectionHeading>
            <div className="flex items-center gap-3 px-4 flex-wrap md:flex-nowrap">
              <span className="text-sm text-gray-900 whitespace-nowrap">{device?.name || '—'}</span>
              <input
                value={nameInput}
                onChange={(e) => setNameInput(e.target.value)}
                maxLength={31}
                title="Logger name: 31 characters maximum"
                placeholder="Change Device Name"
                className="flex-1 text-sm border border-gray-300 rounded px-3 py-1.5 text-gray-700 placeholder-gray-400 focus:outline-none focus:border-[#358ECD]"
              />
              <span className="text-xs text-gray-400 whitespace-nowrap tabular-nums">{nameInput.length}/31</span>
              <ActionFeedback status={action.name} message={action.message} />
              <button
                onClick={onUpdateName}
                disabled={action.name === 'pending' || !nameInput.trim()}
                className="common-size15 px-4 py-1.5 bg-[#F1F2F4] border-gray-300 rounded-md text-sm text-[#4D4D4D] transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Update
              </button>
            </div>
          </Card>

          {/* Firmware card — shows the version currently on the logger and the
              latest available; "Update to Latest" queues an update ONLY to the
              latest version (the server picks it), and is offered only when a
              newer firmware exists. */}
          <Card>
            <SectionHeading>Firmware</SectionHeading>
            <div>
              {[
                { type: 'base', label: 'Logger Firmware', fw: device?.firmware_available?.base, actionKey: 'firmwareBase' },
                { type: 'rtu', label: 'RTU Firmware', fw: device?.firmware_available?.rtu, actionKey: 'firmwareRtu' },
              ].map((row, i) => {
                const fw = row.fw || {}
                const current = fw.current || device?.firmware?.[row.type] || 'Unknown'
                const latest = fw.latest || '—'
                const canUpdate = !!fw.updatable && !!fw.update_available
                const st = action[row.actionKey]
                const pending = st === 'pending'
                const justQueued = st === 'success'   // user clicked Update this session
                // Live progress from the device (fwprogress) takes over once it
                // starts reporting; `justQueued` covers the gap right after click.
                const prog = fwProgressView(fwProgress?.[row.type])
                const updating = !!(prog && prog.updating)
                // Queued = command sent but download not started → cancellable.
                // Active = logger is actually downloading/verifying → not cancellable.
                const isQueued = (prog && prog.queued) || (justQueued && !prog)
                const isActive = updating && !(prog && prog.queued)
                return (
                  <div key={row.type} className={`flex items-center justify-between py-3 gap-3 ${i === 0 ? 'border-b border-gray-100' : ''}`}>
                    <div className="flex items-center gap-2 min-w-0 flex-wrap md:flex-nowrap">
                      <AlertTriangle className={`w-4 h-4 flex-shrink-0 ${canUpdate ? 'text-yellow-500' : 'text-gray-300'}`} />
                      <div className="min-w-0">
                        <div className="text-sm text-gray-700">{row.label}</div>
                        <div className="text-xs text-gray-500">
                          Current: <span className="text-gray-700">{current}</span>
                          <span className="mx-1.5 text-gray-300">|</span>
                          Latest: <span className="text-gray-700">{latest}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      {/* Live progress text (Downloading X% / Verifying / Done /
                          Failed), to the left of the button — matches the design. */}
                      {prog && (
                        <span className={`common-size15 text-sm whitespace-nowrap ${
                          prog.failed ? 'text-red-600' : prog.done ? 'text-[#3B7A2A]' : 'text-[#4D4D4D]'}`}>
                          {prog.text}
                        </span>
                      )}
                      {isQueued ? (
                        <button
                          onClick={() => onCancelFirmware(row.type)}
                          title="Cancel the queued update (the logger hasn't started downloading yet)"
                          className="common-size15 whitespace-nowrap px-4 py-1.5 bg-white border border-red-400 text-red-600 hover:bg-red-50 rounded-md text-sm transition"
                        >
                          Cancel
                        </button>
                      ) : isActive ? (
                        <button
                          disabled
                          title="Firmware update in progress — the logger is downloading over FTP (10-20 min)"
                          className="common-size15 whitespace-nowrap px-4 py-1.5 bg-[#F1F2F4] border-gray-300 rounded-md text-sm text-[#4D4D4D] cursor-not-allowed"
                        >
                          Update
                        </button>
                      ) : canUpdate ? (
                        <button
                          onClick={() => onUpdateFirmware(row.type, latest)}
                          disabled={pending}
                          className="common-size15 whitespace-nowrap px-4 py-1.5 bg-[#358ECD] hover:bg-[#2d7ab5] text-white rounded-md text-sm transition disabled:opacity-50"
                        >
                          {pending ? 'Queuing…' : 'Update to Latest'}
                        </button>
                      ) : (
                        <button
                          disabled
                          title={fw.update_blocked_reason === 'not_ota'
                            ? 'This firmware type is not updatable over the air'
                            : fw.update_blocked_reason === 'min_version'
                              ? `Firmware update is only available on V${fw.min_update_major || 9} or higher`
                              : (fw.latest ? 'The logger is already on the latest firmware' : 'No firmware is available to update to')}
                          className="common-size15 whitespace-nowrap px-4 py-1.5 bg-[#F1F2F4] border-gray-300 rounded-md text-sm text-[#4D4D4D] cursor-not-allowed"
                        >
                          {fw.latest && !fw.update_available ? 'Up to Date' : 'Update to Latest'}
                        </button>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          </Card>

          {/* Action buttons sit directly on the gray background, no card,
              matching the XD design. */}
          <div className="flex flex-wrap justify-between items-center gap-2 md:gap-3 px-0 md:px-8">
            {/* Reset Device (cmd #4). Per the firmware command spec, the
                frontend only exposes a full reset (type 1 = MAIN + RTU);
                resetting MAIN resets the RTU too. The RTU-only option (type 0)
                exists in the protocol but is intentionally not offered here. */}
            <button
              onClick={() => onRunAction('reset', 'Reset this device (MAIN + RTU)? It will restart and reload its settings.', 1)}
              disabled={action.reset === 'pending'}
              className="px-2 py-2 md:px-5 md:py-2 bg-white border-gray-400 rounded-md text-sm text-gray-700 hover:bg-gray-50 transition disabled:opacity-50"
            >
              {action.reset === 'pending' ? 'Resetting…' : 'Reset Device'}
            </button>
            <button
              onClick={() => onRunAction('syncClock')}
              disabled={action.syncClock === 'pending'}
              className="px-2 py-2 md:px-5 md:py-2 bg-white border-gray-400 rounded-md text-sm text-gray-700 hover:bg-gray-50 transition disabled:opacity-50"
            >
              {action.syncClock === 'pending' ? 'Syncing…' : 'Sync Clock'}
            </button>
            {/* Format Memory / Erase (cmd #66, type 0 = erase log data).
                A plain destructive button — no dropdown — that confirms with an
                explicit irreversible-data warning before issuing the command. */}
            <button
              onClick={() => onRunAction(
                'formatMemory',
                'Warning: This will erase all logger data. This operation is irreversible. Please confirm to continue.',
                0,
              )}
              disabled={action.formatMemory === 'pending'}
              className="px-2 py-2 md:px-5 md:py-2 bg-white border border-red-300 rounded-md text-sm text-red-600 hover:bg-red-50 transition disabled:opacity-50"
            >
              {action.formatMemory === 'pending' ? 'Erasing…' : 'Format Memory'}
            </button>
            <ActionFeedback
              status={action.reset !== 'idle' ? action.reset
                : action.formatMemory !== 'idle' ? action.formatMemory
                : action.syncClock !== 'idle' ? action.syncClock : null}
              message={action.message}
            />
          </div>
        </div>

        {/* RIGHT column — Sample/Log/Transmit card */}
        <Card>
          <SectionHeading
            right={
              <label className=" common-size15 flex items-center gap-1.5 text-xs text-[#4D4D4D] cursor-pointer select-none">
                More
                <input type="checkbox" checked={showMore} onChange={(e) => setShowMore(e.target.checked)} className="w-4 h-4 rounded accent-clr" />
              </label>
            }
          >
            Sample/Log/Transmit
          </SectionHeading>

          {!showMore ? (
            // ── Collapsed: one device-wide Log + Transmit row ──
            <table className="w-full text-xs">
              <thead>
                <tr className="text-gray-600">
                  <th className="py-2 text-left font-semibold"></th>
                  <th className="py-2 text-center common-size15 text-[#4D4D4D] font-semibold w-28">Log <span className="font-normal text-[#AFAFAF]">(min)</span></th>
                  <th className="py-2 text-center common-size15 text-[#4D4D4D] font-semibold w-28">Transmit <span className="font-normal text-[#AFAFAF]">(min)</span></th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-t border-gray-100">
                  <td className="common-size15 py-2 text-gray-800 text-[#4D4D4D]">{device?.name || '—'}</td>
                  <td className="py-2 text-center">
                    <input type="number" min="1" max="43200" value={logEdit}
                      onChange={(e) => setLogEdit(e.target.value)} disabled={action.intervals === 'pending'}
                      className="w-16 px-2 py-1 border border-gray-300 rounded text-xs text-center text-gray-700 bg-white focus:outline-none focus:border-[#358ECD] disabled:opacity-50" />
                  </td>
                  <td className="py-2 text-center">
                    <input type="number" min="1" max="43200" value={transmitEdit}
                      onChange={(e) => setTransmitEdit(e.target.value)} disabled={action.intervals === 'pending'}
                      className="w-16 px-2 py-1 border border-gray-300 rounded text-xs text-center text-gray-700 bg-white focus:outline-none focus:border-[#358ECD] disabled:opacity-50" />
                  </td>
                </tr>
              </tbody>
            </table>
          ) : (
            // ── More: per-sensor Sample/Log + device Transmit + per-parameter Transmit Yes/No ──
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-gray-600">
                    <th className="py-2 text-left font-semibold"></th>
                    <th className="py-2 text-center common-size15 text-[#4D4D4D] font-semibold w-28">Transmit <span className="font-normal text-[#AFAFAF]">Yes/No</span></th>
                    <th className="py-2 text-center common-size15 text-[#4D4D4D] font-semibold w-24">Sample <span className="font-normal text-[#AFAFAF]">(min)</span></th>
                    <th className="py-2 text-center common-size15 text-[#4D4D4D] font-semibold w-24">Log <span className="font-normal text-[#AFAFAF]">(min)</span></th>
                    <th className="py-2 text-center common-size15 text-[#4D4D4D] font-semibold w-24">Transmit <span className="font-normal text-[#AFAFAF]">(min)</span></th>
                  </tr>
                </thead>
                <tbody>
                  {sensors.map((sensor) => (
                    <SensorMoreRow
                      key={sensor.id}
                      sensor={sensor}
                      edit={sensorEdits[sensor.id] || {}}
                      onEdit={(field, val) => setSensorEdits((prev) => ({ ...prev, [sensor.id]: { ...prev[sensor.id], [field]: val } }))}
                      flags={flagEdits}
                      setFlag={(pid, val) => setFlagEdits((prev) => ({ ...prev, [pid]: val }))}
                      setFlagsBulk={(ids, val) => setFlagEdits((prev) => { const n = { ...prev }; ids.forEach((id) => { n[id] = val }); return n })}
                      transmitEdit={transmitEdit}
                      setTransmitEdit={setTransmitEdit}
                      expanded={!!expanded[sensor.id]}
                      onToggle={() => toggle(sensor.id)}
                      disabled={action.intervals === 'pending'}
                    />
                  ))}
                  {sensors.length === 0 && (
                    <tr><td colSpan={5} className="py-4 text-center text-gray-400">No sensor configuration available.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          <div className="flex items-center justify-end gap-3 mt-4 px-1">
            <ActionFeedback status={action.intervals} message={action.message} />
            <button
              onClick={showMore ? onApplyMore : onApplyIntervals}
              disabled={action.intervals === 'pending'}
              className="common-size15 px-4 py-1.5 bg-[#F1F2F4] border-gray-300 rounded-md text-sm text-[#4D4D4D] transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Apply
            </button>
          </div>
        </Card>
      </div>
    </div>
  )
}

function SensorMoreRow({ sensor, edit, onEdit, flags, setFlag, setFlagsBulk, transmitEdit, setTransmitEdit, expanded, onToggle, disabled }) {
  const params = sensor.parameters || []
  const hasParams = params.length > 0
  const paramIds = params.map((p) => p.id)
  const allOn = hasParams && paramIds.every((id) => flags[id])
  const someOn = hasParams && paramIds.some((id) => flags[id])
  const numCls = "w-16 px-2 py-1 border border-gray-300 rounded text-xs text-center text-gray-700 bg-white focus:outline-none focus:border-[#358ECD] disabled:opacity-50"
  return (
    <>
      <tr className="border-t border-gray-100 hover:bg-gray-50">
        <td className="py-2">
          <button onClick={onToggle} className="inline-flex items-center gap-1 text-gray-800" disabled={!hasParams}>
            {hasParams ? (expanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />) : <span className="w-3.5 inline-block" />}
            {sensor.name}
          </button>
        </td>
        <td className="py-2 text-center">
          <input type="checkbox" checked={allOn}
            ref={(el) => { if (el) el.indeterminate = someOn && !allOn }}
            onChange={(e) => setFlagsBulk(paramIds, e.target.checked)}
            disabled={disabled || !hasParams}
            className="w-4 h-4 rounded accent-clr" />
        </td>
        <td className="py-2 text-center">
          <input type="number" min="1" max="43200" value={edit.sample ?? ''}
            onChange={(e) => onEdit('sample', e.target.value)} disabled={disabled} className={numCls} />
        </td>
        <td className="py-2 text-center">
          <input type="number" min="1" max="43200" value={edit.log ?? ''}
            onChange={(e) => onEdit('log', e.target.value)} disabled={disabled} className={numCls} />
        </td>
        <td className="py-2 text-center">
          <input type="number" min="1" max="43200" value={transmitEdit}
            onChange={(e) => setTransmitEdit(e.target.value)} disabled={disabled} className={numCls} />
        </td>
      </tr>
      {expanded && params.map((p) => (
        <tr key={p.id} className="border-t border-gray-50 bg-gray-50/40">
          <td className="py-1.5 pl-6 text-gray-600">{p.name}</td>
          <td className="py-1.5 text-center">
            <input type="checkbox" checked={!!flags[p.id]}
              onChange={(e) => setFlag(p.id, e.target.checked)} disabled={disabled}
              className="w-4 h-4 rounded accent-clr" />
          </td>
          <td className="py-1.5 text-center text-gray-300">—</td>
          <td className="py-1.5 text-center text-gray-300">—</td>
          <td className="py-1.5 text-center text-gray-300">—</td>
        </tr>
      ))}
    </>
  )
}
